/**
 * Rendered when another tab in the same browser profile holds the
 * CrossTabLock for this station. Polls every 2 s; when the lock drops we
 * surface a Retry button (don't auto-acquire — the user may have intentionally
 * opened a second tab to inspect the locked state).
 */
import { useEffect } from 'react';
import { useTabLockStore } from '@/state/useTabLockStore';
import { Logo } from '@/ui/components/Logo';
import { formatTime } from '@/lib/format';

export function StationLockedPage({ onRetry }: { onRetry: () => void }): JSX.Element {
  const holder = useTabLockStore((s) => s.holder);
  const status = useTabLockStore((s) => s.status);
  const refresh = useTabLockStore((s) => s.refresh);

  useEffect(() => {
    const id = setInterval(() => {
      void refresh();
    }, 2_000);
    return () => clearInterval(id);
  }, [refresh]);

  return (
    <main
      className="flex min-h-full items-center justify-center bg-slate-950 p-8 text-slate-200"
      data-testid="station-locked-page"
    >
      <article className="max-w-md rounded-2xl border border-amber-700 bg-slate-900 p-8 shadow-2xl">
        <div className="mb-3 flex items-center gap-2">
          <Logo variant="mark" size="sm" testId="locked" />
        </div>
        <h1 className="text-2xl font-semibold tracking-tight text-amber-100">
          Station busy in another tab
        </h1>
        <p className="mt-2 text-sm text-slate-400">
          Another tab in this browser profile holds the connection lock for this station.
        </p>
        {holder ? (
          <dl className="mt-4 grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-slate-300">
            <dt className="text-slate-500">Holder tab</dt>
            <dd className="font-mono">{holder.tabId}</dd>
            <dt className="text-slate-500">Held since</dt>
            <dd>{formatTime(holder.heldSince)}</dd>
            <dt className="text-slate-500">Last heartbeat</dt>
            <dd>{formatTime(holder.lastHeartbeat)}</dd>
          </dl>
        ) : null}
        <p className="mt-6 text-xs text-slate-500" data-testid="lock-status">
          status: {status}
        </p>
        <button
          type="button"
          onClick={onRetry}
          disabled={status === 'foreign'}
          className="mt-6 rounded bg-primary-base px-3 py-1 text-sm font-medium text-primary-on hover:bg-primary-base disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary-base"
          data-testid="retry-acquire"
        >
          Try again
        </button>
      </article>
    </main>
  );
}
